import { AfterViewInit, Component, ElementRef, ViewChild, effect, inject } from '@angular/core';
import { ToastService } from './services/toast.service';
import { ConfirmDialogService } from './services/confirm-dialog.service';
import { PromptDialogService } from './services/prompt-dialog.service';
import { ClienteCoincidenciasService } from './services/cliente-coincidencias.service';

@Component({
  selector: 'app-root',
  template: `
    <router-outlet></router-outlet>

    <!-- NOTIFICACIONES GLOBALES.
         popover="manual" mete el contenedor en el top-layer del navegador,
         de modo que los toasts quedan POR ENCIMA de los <dialog>.showModal()
         (z-index normal no compite contra el top layer). -->
    <div #toastContainer popover="manual" class="toast-popover">
      @for (t of toastService.toasts(); track t.id) {
        <div class="alert shadow-2xl animate-in slide-in-from-right duration-300 pointer-events-auto min-w-[300px]"
             [class.alert-success]="t.type === 'success'"
             [class.alert-error]="t.type === 'error'"
             [class.alert-info]="t.type === 'info'"
             [class.alert-warning]="t.type === 'warning'">
          <div class="flex items-center gap-3 w-full text-white">
            @if (t.type === 'success') { <i class="ri-checkbox-circle-fill text-2xl"></i> }
            @if (t.type === 'error') { <i class="ri-error-warning-fill text-2xl"></i> }
            @if (t.type === 'info') { <i class="ri-information-fill text-2xl"></i> }

            <div class="flex-1">
              <p class="text-sm font-bold">{{ t.message }}</p>
            </div>
            <button (click)="toastService.remove(t.id)" class="btn btn-ghost btn-xs btn-circle text-white">✕</button>
          </div>
        </div>
      }
    </div>

    <!-- CONFIRMACIÓN GLOBAL (reemplaza confirm() nativo).
         Es un <dialog>.showModal() real: al abrirse SOBRE otro modal (p. ej. la
         orden de servicio) se vuelve el modal superior y queda interactivo. Un
         popover, en cambio, queda INERTE encima de un <dialog> modal abierto. -->
    <dialog #confirmContainer class="dialog-modal" (close)="onConfirmClosed()">
      @if (confirmDialog.state(); as cs) {
        @if (cs.open && cs.req; as req) {
          <div class="dialog-backdrop" (click)="confirmDialog.resolve(false)"></div>
          <div class="dialog-card pointer-events-auto">
            <h3 class="text-lg font-bold flex items-center gap-2">
              @if ((req.variant || 'info') === 'danger') { <i class="ri-error-warning-fill text-error text-2xl"></i> }
              @else if (req.variant === 'warning') { <i class="ri-alert-fill text-warning text-2xl"></i> }
              @else { <i class="ri-question-fill text-info text-2xl"></i> }
              {{ req.title }}
            </h3>
            <p class="py-4 whitespace-pre-line">{{ req.message }}</p>
            <div class="modal-action">
              <button type="button" class="btn btn-ghost" (click)="confirmDialog.resolve(false)">
                {{ req.cancelText || 'Cancelar' }}
              </button>
              <button type="button"
                      class="btn"
                      [class.btn-error]="(req.variant || 'info') === 'danger'"
                      [class.btn-warning]="req.variant === 'warning'"
                      [class.btn-primary]="(req.variant || 'info') === 'info'"
                      (click)="confirmDialog.resolve(true)">
                {{ req.confirmText || 'Aceptar' }}
              </button>
            </div>
          </div>
        }
      }
    </dialog>

    <!-- "ESTE CLIENTE YA EXISTE": coincidencias en cualquier sucursal antes de dar
         de alta. Mismo <dialog>.showModal() real para quedar sobre el modal de la OS. -->
    <dialog #coincidenciasContainer class="dialog-modal" (close)="onCoincidenciasClosed()">
      @if (coincidencias.state(); as st) {
        @if (st.open) {
          <div class="dialog-backdrop" (click)="coincidencias.cancelar()"></div>
          <div class="dialog-card pointer-events-auto">
            <h3 class="text-lg font-bold flex items-center gap-2">
              <i class="ri-user-search-fill text-warning text-2xl"></i>
              Este cliente ya podría existir
            </h3>
            <p class="py-2 text-sm opacity-80">
              Los clientes son de todo el taller: si es la misma persona, usa su registro
              para conservar su historial, vehículos y crédito.
            </p>
            <div class="space-y-2 my-3">
              @for (c of st.items; track c.id) {
                <div class="flex items-center gap-3 p-3 rounded-xl border border-base-300 bg-base-200/40">
                  <div class="flex-1 min-w-0">
                    <p class="font-bold truncate">{{ c.nombre }} {{ c.apellido_paterno }} {{ c.apellido_materno || '' }}</p>
                    <p class="text-xs opacity-60 truncate">
                      {{ c.telefono || 'Sin teléfono' }}@if (c.email) { · {{ c.email }} }
                      @if (c.num_vehiculos) { · {{ c.num_vehiculos }} vehículo(s) }
                    </p>
                    <div class="flex flex-wrap gap-1 mt-1">
                      @if (c.sucursalNombre) {
                        <span class="badge badge-ghost badge-xs gap-1"><i class="ri-store-2-line"></i> Alta en {{ c.sucursalNombre }}</span>
                      }
                      @for (m of c.coincide_por; track m) {
                        <span class="badge badge-warning badge-outline badge-xs">{{ coincidencias.motivo(m) }}</span>
                      }
                    </div>
                  </div>
                  <button type="button" class="btn btn-primary btn-sm shrink-0"
                          [disabled]="!!st.usando" (click)="coincidencias.usar(c)">
                    @if (st.usando === c.id) { <span class="loading loading-spinner loading-xs"></span> }
                    Usar este
                  </button>
                </div>
              }
            </div>
            <div class="modal-action flex-wrap">
              <button type="button" class="btn btn-ghost" (click)="coincidencias.cancelar()">Cancelar</button>
              <button type="button" class="btn btn-outline" [disabled]="!!st.usando" (click)="coincidencias.crear()">
                Es otra persona, crear nuevo
              </button>
            </div>
          </div>
        }
      }
    </dialog>

    <!-- PROMPT GLOBAL (reemplaza prompt() nativo). Mismo <dialog>.showModal() real. -->
    <dialog #promptContainer class="dialog-modal" (close)="onPromptClosed()">
      @if (promptDialog.state(); as ps) {
        @if (ps.open && ps.req; as req) {
          <div class="dialog-backdrop" (click)="promptDialog.cancel()"></div>
          <form class="dialog-card pointer-events-auto"
                (submit)="$event.preventDefault(); promptDialog.confirm()">
            <h3 class="text-lg font-bold">{{ req.title }}</h3>
            @if (req.message) {
              <p class="py-2 text-sm opacity-80 whitespace-pre-line">{{ req.message }}</p>
            }
            @if ((req.inputType || 'text') === 'textarea') {
              <textarea
                class="textarea textarea-bordered w-full mt-2"
                rows="3"
                [placeholder]="req.placeholder || ''"
                [value]="ps.value"
                (input)="promptDialog.setValue($any($event.target).value)"
                autofocus></textarea>
            } @else if (req.inputType === 'number') {
              <input
                type="number"
                class="input input-bordered w-full mt-2"
                [placeholder]="req.placeholder || ''"
                [value]="ps.value"
                [attr.min]="req.min ?? null"
                [attr.max]="req.max ?? null"
                [attr.step]="req.step ?? null"
                (input)="promptDialog.setValue($any($event.target).value)"
                autofocus />
            } @else {
              <input
                type="text"
                class="input input-bordered w-full mt-2"
                [placeholder]="req.placeholder || ''"
                [value]="ps.value"
                (input)="promptDialog.setValue($any($event.target).value)"
                autofocus />
            }
            @if (ps.error) {
              <p class="text-error text-sm mt-1">{{ ps.error }}</p>
            }
            <div class="modal-action">
              <button type="button" class="btn btn-ghost" (click)="promptDialog.cancel()">
                {{ req.cancelText || 'Cancelar' }}
              </button>
              <button type="submit" class="btn btn-primary">
                {{ req.confirmText || 'Aceptar' }}
              </button>
            </div>
          </form>
        }
      }
    </dialog>
  `,
  styles: [`
    .toast-popover {
      position: fixed;
      top: 1.25rem;
      right: 1.25rem;
      left: auto;
      bottom: auto;
      margin: 0;
      padding: 0;
      border: none;
      background: transparent;
      max-width: none;
      max-height: none;
      width: auto;
      overflow: visible;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      pointer-events: none;
      z-index: 999999;
    }
    .toast-popover:not(:popover-open) { display: none; }

    .dialog-modal {
      position: fixed;
      inset: 0;
      margin: 0;
      padding: 0;
      border: none;
      background: transparent;
      max-width: 100vw;
      max-height: 100vh;
      width: 100vw;
      height: 100vh;
      overflow: visible;
      z-index: 999998;
    }
    /* El backdrop visible lo pinta el div .dialog-backdrop; el nativo va transparente. */
    .dialog-modal::backdrop { background: transparent; }
    .dialog-backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
      pointer-events: auto;
    }
    .dialog-card {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: var(--color-base-100);
      color: var(--color-base-content);
      border-radius: 1rem;
      padding: 1.5rem;
      min-width: min(420px, 90vw);
      max-width: min(560px, 95vw);
      max-height: 90vh;
      overflow: auto;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    }
  `],
  standalone: false,
})
export class App implements AfterViewInit {
  toastService = inject(ToastService);
  confirmDialog = inject(ConfirmDialogService);
  promptDialog = inject(PromptDialogService);
  coincidencias = inject(ClienteCoincidenciasService);

  @ViewChild('toastContainer') toastContainer?: ElementRef<HTMLElement>;
  @ViewChild('confirmContainer') confirmContainer?: ElementRef<HTMLDialogElement>;
  @ViewChild('promptContainer') promptContainer?: ElementRef<HTMLDialogElement>;
  @ViewChild('coincidenciasContainer') coincidenciasContainer?: ElementRef<HTMLDialogElement>;

  constructor() {
    effect(() => {
      const hasToasts = this.toastService.toasts().length > 0;
      this.togglePopover(this.toastContainer?.nativeElement, hasToasts);
    });
    effect(() => {
      const open = this.confirmDialog.state().open;
      this.toggleDialog(this.confirmContainer?.nativeElement, open);
    });
    effect(() => {
      const open = this.promptDialog.state().open;
      this.toggleDialog(this.promptContainer?.nativeElement, open);
    });
    effect(() => {
      const open = this.coincidencias.state().open;
      this.toggleDialog(this.coincidenciasContainer?.nativeElement, open);
    });
  }

  ngAfterViewInit(): void {
    if (this.toastService.toasts().length > 0) {
      this.togglePopover(this.toastContainer?.nativeElement, true);
    }
  }

  private togglePopover(el: HTMLElement | undefined, open: boolean) {
    if (!el || typeof (el as any).showPopover !== 'function') return;
    try {
      if (open) (el as any).showPopover();
      else (el as any).hidePopover();
    } catch {
      // showPopover lanza si ya está abierto / hidePopover si ya está cerrado.
    }
  }

  /** Abre/cierra un <dialog> real con showModal(). A diferencia de un popover,
   *  un <dialog> modal se apila sobre otros modales y queda interactivo —
   *  un popover queda inerte encima de un <dialog>.showModal() ya abierto.
   *  Usamos setTimeout(0) para garantizar que Angular haya terminado de renderizar
   *  el contenido del @if antes de abrir el diálogo (evita botones no-clickeables
   *  cuando el confirm se invoca desde dentro de otro modal abierto). */
  private toggleDialog(el: HTMLDialogElement | undefined, open: boolean) {
    if (!el || typeof el.showModal !== 'function') return;
    if (!open) {
      try { if (el.open) el.close(); } catch { /* ya cerrado */ }
      return;
    }
    // Abrir en el siguiente microtask para que Angular termine el render del @if
    setTimeout(() => {
      try { if (!el.open) el.showModal(); } catch { /* ya abierto */ }
    }, 0);
  }

  /** ESC o cierre nativo del <dialog> de confirmación ⇒ resolver como cancelado. */
  onConfirmClosed(): void {
    if (this.confirmDialog.state().open) this.confirmDialog.resolve(false);
  }

  /** ESC o cierre nativo del aviso de cliente existente ⇒ cancelar el alta. */
  onCoincidenciasClosed(): void {
    if (this.coincidencias.state().open) this.coincidencias.cancelar();
  }

  /** ESC o cierre nativo del <dialog> de prompt ⇒ resolver como cancelado. */
  onPromptClosed(): void {
    if (this.promptDialog.state().open) this.promptDialog.cancel();
  }
}
